import { c as _c } from "react/compiler-runtime";
// Раздел «Реклама» консоли (этап 8, П1: fd-promo-29, fd-promo-30, fd-platform-07).
//
// Вкладки:
//   • «Модерация» — очередь кампаний на проверке с таймером SLA (48 ч по
//     умолчанию); просроченные сверху и красным;
//   • «Кампании» — все кампании площадки с фильтрами;
//   • «Отчёт» — показы, клики, CTR, конверсии, расход, ROI по всем кампаниям;
//   • «Атрибуции» — журнал: засчитан ли заказ рекламе, какой кампании, по
//     какому клику, и состояние удержания CPA;
//   • «Настройки» — ставки пакетов CPA, пороги CPM, окно атрибуции, холд, SLA.
import type { AdAttributionState, AdCampaignStatus, AdModel, AdSettings } from "@osmix/contracts";
import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { adsAvailable, dateOnly, dateTime, lastDays, modelLabel, PACKAGE_LABEL, pct, rateLabel, REVERSE_LABEL, rub, slaLabel, STATE_LABEL, STATUS_LABEL, useAdAttributions, useAdminCampaigns, useAdModeration, useAdReport, useAdSettingsAdmin } from "./ad-campaigns-api";
type Tab = "moderation" | "campaigns" | "report" | "attributions" | "settings";
const TABS: {
  key: Tab;
  label: string;
}[] = [{
  key: "moderation",
  label: "Модерация"
}, {
  key: "campaigns",
  label: "Кампании"
}, {
  key: "report",
  label: "Отчёт"
}, {
  key: "attributions",
  label: "Атрибуции"
}, {
  key: "settings",
  label: "Настройки"
}];
export const STATUS_CLASS: Record<AdCampaignStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_moderation: "bg-amber-100 text-amber-800",
  approved: "bg-sky-100 text-sky-800",
  rejected: "bg-red-100 text-red-700",
  active: "bg-emerald-100 text-emerald-800",
  paused: "bg-muted text-foreground",
  completed: "bg-muted text-muted-foreground"
};
export function StatusBadge(t0) {
  const $ = _c(5);
  const {
    status
  } = t0;
  const t1 = STATUS_CLASS[status];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = STATUS_LABEL[status];
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export function AdCampaignsPage() {
  const $ = _c(29);
  const [params, setParams] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = params.get("tab") as Tab || "moderation";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tab = t0;
  let t1;
  if ($[2] !== params || $[3] !== setParams) {
    t1 = next => {
      const p = new URLSearchParams(params);
      if (next === "moderation") {
        p.delete("tab");
      } else {
        p.set("tab", next);
      }
      setParams(p, {
        replace: true
      });
    };
    $[2] = params;
    $[3] = setParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setTab = t1;
  if (!adsAvailable) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="py-16 text-center text-sm text-muted-foreground">Раздел работает при подключении к шлюзу.</p>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div><h1 className="text-xl font-semibold">Реклама</h1><p className="text-sm text-muted-foreground">Кампании продавцов CPA и CPM: модерация, отчёт, журнал атрибуций заказов и ставки пакетов.</p></div>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== setTab || $[8] !== tab) {
    t3 = TABS.map(t => <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`ads-tab-${t.key}`} onClick={() => setTab(t.key)} className={cn("-mb-px shrink-0 border-b-2 px-4 py-2 text-sm", tab === t.key ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t.label}</button>);
    $[7] = setTab;
    $[8] = tab;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div className="flex gap-1 overflow-x-auto border-b" role="tablist">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== tab) {
    t5 = tab === "moderation" && <CampaignTable fixedStatus="pending_moderation" />;
    $[12] = tab;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== tab) {
    t6 = tab === "campaigns" && <CampaignTable />;
    $[14] = tab;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== tab) {
    t7 = tab === "report" && <ReportTab />;
    $[16] = tab;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== tab) {
    t8 = tab === "attributions" && <AttributionsTab />;
    $[18] = tab;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== tab) {
    t9 = tab === "settings" && <SettingsTab />;
    $[20] = tab;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = <div className="flex flex-col gap-6">{t2}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}

// ── Очередь и список ────────────────────────────────────────────────────────

function CampaignTable(t0) {
  const $ = _c(45);
  const {
    fixedStatus
  } = t0;
  const [status, setStatus] = useState(fixedStatus ?? "all");
  const [model, setModel] = useState("");
  const [q, setQ] = useState("");
  const t1 = fixedStatus ?? status;
  let t2;
  if ($[0] !== model || $[1] !== q || $[2] !== t1) {
    t2 = {
      status: t1,
      model,
      q
    };
    $[0] = model;
    $[1] = q;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data,
    isLoading,
    error
  } = useAdminCampaigns(t2);
  let t3;
  if ($[4] !== data) {
    t3 = data ?? [];
    $[4] = data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const list = t3;
  const moderation = fixedStatus === "pending_moderation";
  let t4;
  if ($[6] !== fixedStatus || $[7] !== model || $[8] !== q || $[9] !== status) {
    t4 = !fixedStatus && <div className="flex flex-wrap items-center gap-2"><div className="relative w-full max-w-xs"><Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0438\u043B\u0438 id \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438"} className="pl-8" /></div><Select value={status} onChange={e_0 => setStatus(e_0.target.value as AdCampaignStatus | "all")} aria-label={"\u0421\u0442\u0430\u0442\u0443\u0441"}><option value="all">Все статусы</option>{(Object.keys(STATUS_LABEL) as AdCampaignStatus[]).map(_temp)}</Select><Select value={model} onChange={e_1 => setModel(e_1.target.value as AdModel | "")} aria-label={"\u041C\u043E\u0434\u0435\u043B\u044C"}><option value="">CPA и CPM</option><option value="cpa">CPA</option><option value="cpm">CPM</option></Select></div>;
    $[6] = fixedStatus;
    $[7] = model;
    $[8] = q;
    $[9] = status;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== moderation) {
    t5 = moderation && <p className="text-sm text-muted-foreground">Срок решения - от отправки продавцом. Проверьте товары и описание; отказ - только с причиной, продавец её увидит.</p>;
    $[11] = moderation;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== error) {
    t6 = error && <p className="text-sm text-destructive">Не удалось загрузить кампании: {(error as Error).message}</p>;
    $[13] = error;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TableHead>Кампания</TableHead>;
    t8 = <TableHead>Магазин</TableHead>;
    t9 = <TableHead>Модель</TableHead>;
    t10 = <TableHead className="text-right">Бюджет</TableHead>;
    t11 = <TableHead>Период</TableHead>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[15];
    t11 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t12;
  if ($[20] !== moderation) {
    t12 = <TableHeader><TableRow>{t7}{t8}{t9}{t10}{t11}{moderation ? <TableHead>Срок модерации</TableHead> : <TableHead>Статус</TableHead>}</TableRow></TableHeader>;
    $[20] = moderation;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== isLoading) {
    t13 = isLoading && <TableRow><TableCell colSpan={6} className="h-20 text-center text-muted-foreground"><Loader2 className="mx-auto size-4 animate-spin" /></TableCell></TableRow>;
    $[22] = isLoading;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== isLoading || $[25] !== list.length || $[26] !== moderation) {
    t14 = !isLoading && list.length === 0 && <TableRow><TableCell colSpan={6} className="h-20 text-center text-muted-foreground">{moderation ? "\u041E\u0447\u0435\u0440\u0435\u0434\u044C \u043F\u0443\u0441\u0442\u0430 - \u0432\u0441\u0435 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u043F\u0440\u043E\u0432\u0435\u0440\u0435\u043D\u044B." : "\u041A\u0430\u043C\u043F\u0430\u043D\u0438\u0439 \u043D\u0435\u0442."}</TableCell></TableRow>;
    $[24] = isLoading;
    $[25] = list.length;
    $[26] = moderation;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== list || $[29] !== moderation) {
    let t16;
    if ($[31] !== moderation) {
      t16 = c => {
        const sla = slaLabel(c.slaDueAt);
        return <TableRow key={c.id} data-testid="ads-row"><TableCell><Link to={`/ad-campaigns/${c.id}`} className="font-medium hover:underline">{c.title}</Link><div className="text-xs text-muted-foreground">{c.productIds.length} тов.</div></TableCell><TableCell className="max-w-[220px] truncate">{c.shopName ?? c.shopId}</TableCell><TableCell className="whitespace-nowrap">{modelLabel(c)}<div className="text-xs text-muted-foreground">{rateLabel(c)}</div></TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(c.spent)} / {rub(c.budgetTotal)}</TableCell><TableCell className="whitespace-nowrap">{dateOnly(c.startsAt)} - {dateOnly(c.endsAt)}</TableCell><TableCell className="whitespace-nowrap">{moderation ? <span className={cn("text-sm", sla.overdue ? "font-medium text-destructive" : "text-foreground")} title={dateTime(c.slaDueAt)}>{sla.text}</span> : <StatusBadge status={c.status} />}</TableCell></TableRow>;
      };
      $[31] = moderation;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    t15 = list.map(t16);
    $[28] = list;
    $[29] = moderation;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = <TableBody>{t13}{t14}{t15}</TableBody>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t12 || $[38] !== t16) {
    t17 = <div className="overflow-x-auto rounded-lg border"><Table>{t12}{t16}</Table></div>;
    $[37] = t12;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t18 = <div className="flex flex-col gap-3">{t4}{t5}{t6}{t17}</div>;
    $[40] = t17;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}

// ── Отчёт ──────────────────────────────────────────────────────────────────
function _temp(s) {
  return <option key={s} value={s}>{STATUS_LABEL[s]}</option>;
}
const REPORT_DAYS = [7, 30, 90] as const;
function ReportTab() {
  const $ = _c(69);
  const [days, setDays] = useState(30);
  let t0;
  if ($[0] !== days) {
    t0 = lastDays(days);
    $[0] = days;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const range = t0;
  const {
    data,
    isLoading,
    error
  } = useAdReport(range);
  const totals = data?.totals;
  const t1 = totals?.impressions ?? 0;
  let t2;
  if ($[2] !== t1) {
    t2 = t1.toLocaleString("ru-RU");
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = ["\u041F\u043E\u043A\u0430\u0437\u044B", t2];
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = totals?.clicks ?? 0;
  let t5;
  if ($[6] !== t4) {
    t5 = t4.toLocaleString("ru-RU");
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = ["\u041A\u043B\u0438\u043A\u0438", t5];
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = totals?.ctr ?? null;
  let t8;
  if ($[10] !== t7) {
    t8 = pct(t7, 2);
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = ["CTR", t8];
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = totals?.conversions ?? 0;
  let t11;
  if ($[14] !== t10) {
    t11 = t10.toLocaleString("ru-RU");
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = ["\u041A\u043E\u043D\u0432\u0435\u0440\u0441\u0438\u0438", t11];
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = totals?.revenue ?? 0;
  let t14;
  if ($[18] !== t13) {
    t14 = rub(t13);
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t14) {
    t15 = ["\u0412\u044B\u0440\u0443\u0447\u043A\u0430", t14];
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = totals?.spend ?? 0;
  let t17;
  if ($[22] !== t16) {
    t17 = rub(t16);
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t17) {
    t18 = ["\u0420\u0430\u0441\u0445\u043E\u0434 (\u0434\u043E\u0445\u043E\u0434 \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0438)", t17];
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = totals?.roi ?? null;
  let t20;
  if ($[26] !== t19) {
    t20 = pct(t19, 0);
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t20) {
    t21 = ["ROI", t20];
    $[28] = t20;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t12 || $[31] !== t15 || $[32] !== t18 || $[33] !== t21 || $[34] !== t3 || $[35] !== t6 || $[36] !== t9) {
    t22 = [t3, t6, t9, t12, t15, t18, t21];
    $[30] = t12;
    $[31] = t15;
    $[32] = t18;
    $[33] = t21;
    $[34] = t3;
    $[35] = t6;
    $[36] = t9;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const cards = t22;
  const t23 = String(days);
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e => setDays(Number(e.target.value));
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = REPORT_DAYS.map(_temp2);
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t23) {
    t26 = <Select value={t23} onChange={t24} aria-label={"\u041F\u0435\u0440\u0438\u043E\u0434"}>{t25}</Select>;
    $[40] = t23;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== range.from || $[43] !== range.to) {
    t27 = <span className="text-sm text-muted-foreground">{range.from} - {range.to}</span>;
    $[42] = range.from;
    $[43] = range.to;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t26 || $[46] !== t27) {
    t28 = <div className="flex items-center gap-2">{t26}{t27}</div>;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== error) {
    t29 = error && <p className="text-sm text-destructive">{(error as Error).message}</p>;
    $[48] = error;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== cards || $[51] !== isLoading) {
    t30 = <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="ads-report-totals">{cards.map(t31 => {
        const [label, value] = t31;
        return <div key={label} className="rounded-lg border p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 text-lg font-semibold tabular-nums">{isLoading ? "\u2026" : value}</div></div>;
      })}</div>;
    $[50] = cards;
    $[51] = isLoading;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <TableHeader><TableRow><TableHead>Кампания</TableHead><TableHead>Магазин</TableHead><TableHead>Статус</TableHead><TableHead className="text-right">Показы</TableHead><TableHead className="text-right">Клики</TableHead><TableHead className="text-right">CTR</TableHead><TableHead className="text-right">Конверсии</TableHead><TableHead className="text-right">Выручка</TableHead><TableHead className="text-right">Расход</TableHead><TableHead className="text-right">ROI</TableHead></TableRow></TableHeader>;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== data?.byCampaign) {
    t32 = data?.byCampaign ?? [];
    $[54] = data?.byCampaign;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t32) {
    t33 = t32.map(_temp3);
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== data?.byCampaign || $[59] !== isLoading) {
    t34 = !isLoading && (data?.byCampaign ?? []).length === 0 && <TableRow><TableCell colSpan={10} className="h-20 text-center text-muted-foreground">Кампаний нет.</TableCell></TableRow>;
    $[58] = data?.byCampaign;
    $[59] = isLoading;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t33 || $[62] !== t34) {
    t35 = <div className="overflow-x-auto rounded-lg border"><Table>{t31}<TableBody>{t33}{t34}</TableBody></Table></div>;
    $[61] = t33;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== t28 || $[65] !== t29 || $[66] !== t30 || $[67] !== t35) {
    t36 = <div className="flex flex-col gap-4">{t28}{t29}{t30}{t35}</div>;
    $[64] = t28;
    $[65] = t29;
    $[66] = t30;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  return t36;
}

// ── Журнал атрибуций ──────────────────────────────────────────────────────
function _temp3(row) {
  return <TableRow key={row.campaignId}><TableCell><Link to={`/ad-campaigns/${row.campaignId}`} className="font-medium hover:underline">{row.title}</Link><div className="text-xs text-muted-foreground">{row.model.toUpperCase()}</div></TableCell><TableCell className="max-w-[200px] truncate">{row.shopName ?? row.shopId}</TableCell><TableCell><StatusBadge status={row.status} /></TableCell><TableCell className="text-right tabular-nums">{row.impressions.toLocaleString("ru-RU")}</TableCell><TableCell className="text-right tabular-nums">{row.clicks.toLocaleString("ru-RU")}</TableCell><TableCell className="text-right tabular-nums">{pct(row.ctr, 2)}</TableCell><TableCell className="text-right tabular-nums">{row.conversions}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(row.revenue)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(row.spend)}</TableCell><TableCell className="text-right tabular-nums">{pct(row.roi, 0)}</TableCell></TableRow>;
}
function _temp2(d) {
  return <option key={d} value={d}>{d} дней</option>;
}
function AttributionsTab() {
  const $ = _c(45);
  const [params] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = params.get("orderId") ?? "";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [orderId, setOrderId] = useState(t0);
  const [state, setState] = useState("");
  let t1;
  if ($[2] !== orderId) {
    t1 = orderId.trim();
    $[2] = orderId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== params) {
    t2 = params.get("campaignId") ?? undefined;
    $[4] = params;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== state || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      orderId: t1,
      campaignId: t2,
      state
    };
    $[6] = state;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data,
    isLoading,
    error
  } = useAdAttributions(t3);
  let t4;
  if ($[10] !== data) {
    t4 = data ?? [];
    $[10] = data;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const rows = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e => setOrderId(e.target.value);
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== orderId) {
    t7 = <div className="relative w-full max-w-xs">{t5}<Input value={orderId} onChange={t6} placeholder={"\u041D\u043E\u043C\u0435\u0440 \u0438\u043B\u0438 id \u0437\u0430\u043A\u0430\u0437\u0430"} className="pl-8" /></div>;
    $[14] = orderId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_0 => setState(e_0.target.value as AdAttributionState | "");
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <option value="">Все состояния</option>;
    t10 = Object.keys(STATE_LABEL);
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (t10 as AdAttributionState[]).map(_temp4);
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== state) {
    t12 = <Select value={state} onChange={t8} aria-label={"\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435"}>{t9}{t11}</Select>;
    $[20] = state;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t7) {
    t13 = <div className="flex flex-wrap items-center gap-2">{t7}{t12}</div>;
    $[22] = t12;
    $[23] = t7;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="text-sm text-muted-foreground">Заказ засчитывается рекламе по последнему клику по этому товару в окне атрибуции. CPA-комиссия удерживается после доставки и холда; у CPM конверсия - только для отчёта.</p>;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== error) {
    t15 = error && <p className="text-sm text-destructive">{(error as Error).message}</p>;
    $[26] = error;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <TableHeader><TableRow><TableHead>Заказ</TableHead><TableHead>Товар</TableHead><TableHead>Кампания</TableHead><TableHead>Клик</TableHead><TableHead className="text-right">Сумма позиции</TableHead><TableHead className="text-right">Комиссия</TableHead><TableHead>Состояние</TableHead></TableRow></TableHeader>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== isLoading) {
    t17 = isLoading && <TableRow><TableCell colSpan={7} className="h-20 text-center"><Loader2 className="mx-auto size-4 animate-spin" /></TableCell></TableRow>;
    $[29] = isLoading;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== isLoading || $[32] !== orderId || $[33] !== rows.length) {
    t18 = !isLoading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="h-20 text-center text-muted-foreground">{orderId.trim() ? "\u0417\u0430\u043A\u0430\u0437 \u0440\u0435\u043A\u043B\u0430\u043C\u0435 \u043D\u0435 \u0437\u0430\u0441\u0447\u0438\u0442\u0430\u043D: \u043A\u043B\u0438\u043A\u0430 \u043F\u043E \u0440\u0435\u043A\u043B\u0430\u043C\u0435 \u0435\u0433\u043E \u0442\u043E\u0432\u0430\u0440\u043E\u0432 \u0432 \u043E\u043A\u043D\u0435 \u0430\u0442\u0440\u0438\u0431\u0443\u0446\u0438\u0438 \u043D\u0435 \u0431\u044B\u043B\u043E." : "\u0417\u0430\u0441\u0447\u0438\u0442\u0430\u043D\u043D\u044B\u0445 \u0440\u0435\u043A\u043B\u0430\u043C\u0435 \u0437\u0430\u043A\u0430\u0437\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442."}</TableCell></TableRow>;
    $[31] = isLoading;
    $[32] = orderId;
    $[33] = rows.length;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== rows) {
    t19 = rows.map(_temp5);
    $[35] = rows;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t17 || $[38] !== t18 || $[39] !== t19) {
    t20 = <div className="overflow-x-auto rounded-lg border"><Table>{t16}<TableBody>{t17}{t18}{t19}</TableBody></Table></div>;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t13 || $[42] !== t15 || $[43] !== t20) {
    t21 = <div className="flex flex-col gap-3">{t13}{t14}{t15}{t20}</div>;
    $[41] = t13;
    $[42] = t15;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  return t21;
}

// ── Настройки ──────────────────────────────────────────────────────────────
function _temp5(a) {
  return <TableRow key={`${a.orderId}:${a.orderLineId}`} data-testid="ads-attribution-row"><TableCell className="whitespace-nowrap"><Link to={`/orders?q=${encodeURIComponent(String(a.orderNumber ?? a.orderId))}`} className="font-medium hover:underline">№ {a.orderNumber ?? a.orderId}</Link><div className="text-xs text-muted-foreground">{dateTime(a.orderCreatedAt)} · {a.orderLineId}</div></TableCell><TableCell className="max-w-[220px] truncate">{a.productName}<div className="text-xs text-muted-foreground">{a.shopName}</div></TableCell><TableCell><Link to={`/ad-campaigns/${a.campaignId}`} className="hover:underline">{a.campaignTitle}</Link><div className="text-xs text-muted-foreground">{a.model.toUpperCase()}</div></TableCell><TableCell className="whitespace-nowrap">{dateTime(a.clickedAt)}<div className="font-mono text-[11px] text-muted-foreground">{a.clickEventId ?? "-"}</div></TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{rub(a.amountBase)}</TableCell><TableCell className="whitespace-nowrap text-right tabular-nums">{a.model === "cpa" ? `${rub(a.charge)} (${pct(a.rate)})` : "-"}</TableCell><TableCell className="whitespace-nowrap">{STATE_LABEL[a.state]}{a.reverseReason && <div className="text-xs text-muted-foreground">{REVERSE_LABEL[a.reverseReason] ?? a.reverseReason}</div>}{a.state === "held" && a.heldAt && <div className="text-xs text-muted-foreground">с {dateTime(a.heldAt)}</div>}{a.state === "charged" && a.chargedAt && <div className="text-xs text-muted-foreground">{dateTime(a.chargedAt)}</div>}</TableCell></TableRow>;
}
function _temp4(s) {
  return <option key={s} value={s}>{STATE_LABEL[s]}</option>;
}
type Draft = {
  rates: Record<keyof AdSettings["cpaPackages"], string>;
  minBudgets: Record<keyof AdSettings["cpaPackages"], string>;
  cpmMinBid: string;
  cpmMinBudget: string;
  attributionWindowDays: string;
  holdDays: string;
  moderationSlaHours: string;
};
const PACKAGES = ["basic", "extended", "maximum"] as const;
const toDraft = (s: AdSettings): Draft => ({
  rates: {
    basic: String(Math.round(s.cpaPackages.basic.rate * 10000) / 100),
    extended: String(Math.round(s.cpaPackages.extended.rate * 10000) / 100),
    maximum: String(Math.round(s.cpaPackages.maximum.rate * 10000) / 100)
  },
  minBudgets: {
    basic: String(s.cpaPackages.basic.minBudget),
    extended: String(s.cpaPackages.extended.minBudget),
    maximum: String(s.cpaPackages.maximum.minBudget)
  },
  cpmMinBid: String(s.cpmMinBid),
  cpmMinBudget: String(s.cpmMinBudget),
  attributionWindowDays: String(s.attributionWindowDays),
  holdDays: String(s.holdDays),
  moderationSlaHours: String(s.moderationSlaHours)
});
const numberOf = (v: string) => Number(v.replace(",", ".").replace(/\s/g, ""));
function SettingsTab() {
  const $ = _c(79);
  const t0 = useAuth(_temp6);
  let t1;
  if ($[0] !== t0) {
    t1 = hasAdminPermission(t0, "action.ads.moderate");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canEdit = t1;
  const {
    data
  } = useAdSettingsAdmin();
  const {
    saveSettings
  } = useAdModeration();
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState(null);
  let t2;
  let t3;
  if ($[2] !== data || $[3] !== draft) {
    t2 = () => {
      if (data && !draft) {
        setDraft(toDraft(data));
      }
    };
    t3 = [data, draft];
    $[2] = data;
    $[3] = draft;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (!draft) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loader2 className="size-4 animate-spin" />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t4;
  if ($[7] !== canEdit) {
    t4 = (label, value, onChange, hint, testId) => {
      const id = `ads-setting-${label.replace(/[^a-zа-я0-9]+/gi, "-")}`;
      return <div className="flex flex-col gap-1"><label htmlFor={id} className="text-sm">{label}</label><Input id={id} value={value} disabled={!canEdit} onChange={e => onChange(e.target.value)} inputMode="decimal" data-testid={testId} />{hint && <span className="text-xs text-muted-foreground">{hint}</span>}</div>;
    };
    $[7] = canEdit;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const field = t4;
  let t5;
  if ($[9] !== draft.attributionWindowDays || $[10] !== draft.cpmMinBid || $[11] !== draft.cpmMinBudget || $[12] !== draft.holdDays || $[13] !== draft.minBudgets || $[14] !== draft.moderationSlaHours || $[15] !== draft.rates || $[16] !== saveSettings) {
    t5 = async () => {
      setMessage(null);
      const patch = {
        cpaPackages: Object.fromEntries(PACKAGES.map(p => [p, {
          rate: numberOf(draft.rates[p]) / 100,
          minBudget: numberOf(draft.minBudgets[p])
        }])),
        cpmMinBid: numberOf(draft.cpmMinBid),
        cpmMinBudget: numberOf(draft.cpmMinBudget),
        attributionWindowDays: Math.round(numberOf(draft.attributionWindowDays)),
        holdDays: Math.round(numberOf(draft.holdDays)),
        moderationSlaHours: Math.round(numberOf(draft.moderationSlaHours))
      };
      ;
      try {
        const next = await saveSettings.mutateAsync(patch);
        setDraft(toDraft(next));
        setMessage({
          ok: true,
          text: "\u0421\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u043E. \u041D\u043E\u0432\u044B\u0435 \u0441\u0442\u0430\u0432\u043A\u0438 \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u044E\u0442 \u0434\u043B\u044F \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439, \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u043D\u044B\u0445 \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E \u043F\u043E\u0441\u043B\u0435 \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F."
        });
      } catch (t6) {
        const e_0 = t6;
        setMessage({
          ok: false,
          text: (e_0 as Error).message
        });
      }
    };
    $[9] = draft.attributionWindowDays;
    $[10] = draft.cpmMinBid;
    $[11] = draft.cpmMinBudget;
    $[12] = draft.holdDays;
    $[13] = draft.minBudgets;
    $[14] = draft.moderationSlaHours;
    $[15] = draft.rates;
    $[16] = saveSettings;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const save = t5;
  let t6;
  if ($[18] !== canEdit) {
    t6 = !canEdit && <p className="text-sm text-muted-foreground">Менять настройки может роль с правом «Модерация рекламы».</p>;
    $[18] = canEdit;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h2 className="font-medium">Пакеты CPA</h2>;
    t8 = <p className="text-sm text-muted-foreground">Ставки по ТЗ (OSMIX-Req-v3): 5 / 6 / 7,5 %. Удерживаются с доставленного заказа после холда.</p>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  let t9;
  if ($[22] !== draft || $[23] !== field) {
    t9 = PACKAGES.map(p_0 => <div key={p_0} className="flex flex-col gap-3 rounded-lg border p-4"><span className="text-sm font-medium">{PACKAGE_LABEL[p_0]}</span>{field("\u0421\u0442\u0430\u0432\u043A\u0430, %", draft.rates[p_0], v => setDraft({
        ...draft,
        rates: {
          ...draft.rates,
          [p_0]: v
        }
      }), undefined, `ads-rate-${p_0}`)}{field("\u041C\u0438\u043D. \u0431\u044E\u0434\u0436\u0435\u0442, \u0440\u0443\u0431.", draft.minBudgets[p_0], v_0 => setDraft({
        ...draft,
        minBudgets: {
          ...draft.minBudgets,
          [p_0]: v_0
        }
      }))}</div>);
    $[22] = draft;
    $[23] = field;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t9) {
    t10 = <section className="flex flex-col gap-3">{t7}{t8}<div className="grid gap-4 sm:grid-cols-3">{t9}</div></section>;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <h2 className="font-medium sm:col-span-2">CPM</h2>;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== draft) {
    t12 = v_1 => setDraft({
      ...draft,
      cpmMinBid: v_1
    });
    $[28] = draft;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== draft.cpmMinBid || $[31] !== field || $[32] !== t12) {
    t13 = field("\u041C\u0438\u043D. \u0441\u0442\u0430\u0432\u043A\u0430, \u0440\u0443\u0431. \u0437\u0430 1000 \u043F\u043E\u043A\u0430\u0437\u043E\u0432", draft.cpmMinBid, t12);
    $[30] = draft.cpmMinBid;
    $[31] = field;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== draft) {
    t14 = v_2 => setDraft({
      ...draft,
      cpmMinBudget: v_2
    });
    $[34] = draft;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== draft.cpmMinBudget || $[37] !== field || $[38] !== t14) {
    t15 = field("\u041C\u0438\u043D. \u0431\u044E\u0434\u0436\u0435\u0442 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438, \u0440\u0443\u0431.", draft.cpmMinBudget, t14);
    $[36] = draft.cpmMinBudget;
    $[37] = field;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t13 || $[41] !== t15) {
    t16 = <section className="grid gap-4 sm:grid-cols-2">{t11}{t13}{t15}</section>;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h2 className="font-medium sm:col-span-3">Атрибуция и модерация</h2>;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== draft) {
    t18 = v_3 => setDraft({
      ...draft,
      attributionWindowDays: v_3
    });
    $[44] = draft;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== draft.attributionWindowDays || $[47] !== field || $[48] !== t18) {
    t19 = field("\u041E\u043A\u043D\u043E \u0430\u0442\u0440\u0438\u0431\u0443\u0446\u0438\u0438, \u0434\u043D\u0435\u0439", draft.attributionWindowDays, t18, "\u0421\u043A\u043E\u043B\u044C\u043A\u043E \u0434\u043D\u0435\u0439 \u043F\u043E\u0441\u043B\u0435 \u043A\u043B\u0438\u043A\u0430 \u0437\u0430\u043A\u0430\u0437 \u0437\u0430\u0441\u0447\u0438\u0442\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u0440\u0435\u043A\u043B\u0430\u043C\u0435", "ads-window");
    $[46] = draft.attributionWindowDays;
    $[47] = field;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== draft) {
    t20 = v_4 => setDraft({
      ...draft,
      holdDays: v_4
    });
    $[50] = draft;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== draft.holdDays || $[53] !== field || $[54] !== t20) {
    t21 = field("\u0425\u043E\u043B\u0434 \u043F\u043E\u0441\u043B\u0435 \u0434\u043E\u0441\u0442\u0430\u0432\u043A\u0438, \u0434\u043D\u0435\u0439", draft.holdDays, t20, "\u0427\u0435\u0440\u0435\u0437 \u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0434\u043D\u0435\u0439 \u0443\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u0442\u0441\u044F CPA-\u043A\u043E\u043C\u0438\u0441\u0441\u0438\u044F");
    $[52] = draft.holdDays;
    $[53] = field;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== draft) {
    t22 = v_5 => setDraft({
      ...draft,
      moderationSlaHours: v_5
    });
    $[56] = draft;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== draft.moderationSlaHours || $[59] !== field || $[60] !== t22) {
    t23 = field("\u0421\u0440\u043E\u043A \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u0438, \u0447\u0430\u0441\u043E\u0432", draft.moderationSlaHours, t22);
    $[58] = draft.moderationSlaHours;
    $[59] = field;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t19 || $[63] !== t21 || $[64] !== t23) {
    t24 = <section className="grid gap-4 sm:grid-cols-3">{t17}{t19}{t21}{t23}</section>;
    $[62] = t19;
    $[63] = t21;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== message) {
    t25 = message && <p className={cn("text-sm", message.ok ? "text-emerald-700" : "text-destructive")}>{message.text}</p>;
    $[66] = message;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== canEdit || $[69] !== save || $[70] !== saveSettings) {
    t26 = canEdit && <div><Button onClick={() => void save()} disabled={saveSettings.isPending}>{saveSettings.isPending && <Loader2 className="size-4 animate-spin" />}Сохранить настройки</Button></div>;
    $[68] = canEdit;
    $[69] = save;
    $[70] = saveSettings;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t10 || $[73] !== t16 || $[74] !== t24 || $[75] !== t25 || $[76] !== t26 || $[77] !== t6) {
    t27 = <div className="flex max-w-3xl flex-col gap-6">{t6}{t10}{t16}{t24}{t25}{t26}</div>;
    $[72] = t10;
    $[73] = t16;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
    $[77] = t6;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  return t27;
}
function _temp6(s) {
  return s.user;
}